import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { PortalContainer } from '../../components/PortalContainer';
import { Typography } from '@mui/material';
import Loader from '@tcl-boron-prefabs/loader';
import { AllReportStatuses } from '@tempus/portal-service-shared';
import { Button } from '@tcl-boron-prefabs/button';
import { useHistory } from 'react-router-dom';
import logger from '../../../../utils/logger';
import ErrorScreen from '../../components/ErrorScreen';
import usePortalContext from '../../context/usePortalContext';
import { hasQnsReport } from './utils/hasQnsReport';
import ReportStatusView from './components/ReportStatusView';
import ReportLandingView from './components/ReportLandingView';

export const Home: FunctionComponent = () => {
  const {
    patient: { orderId, reports },
    fetchHelper,
  } = usePortalContext();
  const [reportStatus, setReportStatus] = useState<AllReportStatuses>();

  const hasQns = hasQnsReport(reports);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [showLanding, setShowLanding] = useState<boolean>(hasQns);

  const { push } = useHistory();

  const fetchReportStatus = useCallback(async () => {
    const seenLanding = sessionStorage.getItem('seenLanding');
    try {
      const response = await fetchHelper<AllReportStatuses>(`/orders/${orderId}/report-status`);
      setReportStatus(response);
      if (seenLanding === 'true') {
        setShowLanding(false);
      } else {
        setShowLanding(hasQns || response === AllReportStatuses.COMPLETE);
      }
    } catch (e) {
      setError(true);
      logger.error(e as Error, 'Error fetching report status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReportStatus();
  }, []);

  return (
    <PortalContainer>
      <Grid
        container
        flexGrow={1}
        direction="column"
        justifyContent={{
          xs: 'start',
          md: 'center',
        }}
        alignItems={{
          xs: 'stretch',
          md: 'center',
        }}
        maxWidth={1280}
        margin="0 auto"
      >
        {loading ? (
          <Grid container flexGrow={1} direction="column" gap="8px" alignItems="center" justifyContent="center">
            <Loader size="large" />
            <Typography variant="body1">Loading</Typography>
          </Grid>
        ) : error ? (
          <Grid container alignItems="center" justifyContent="center" flexGrow={1}>
            <ErrorScreen />
          </Grid>
        ) : (
          <>
            <Grid
              container
              direction="column"
              padding="30px 0px"
              gap="12px"
              flexGrow={1}
              width={{
                xs: '100%',
                md: 'fit-content',
              }}
            >
              {showLanding ? (
                <ReportLandingView isQNS={hasQns} setShowLanding={setShowLanding} />
              ) : (
                <ReportStatusView reportStatus={reportStatus} hasQns={hasQns} />
              )}
              {reportStatus === AllReportStatuses.COMPLETE && (
                <Grid item container paddingTop="30px" justifyContent="center">
                  <Button buttonType="primary" onClick={() => push('/report')} minWidth="250px" ariaLabel="View Report">
                    View my report
                  </Button>
                </Grid>
              )}
            </Grid>
          </>
        )}
      </Grid>
    </PortalContainer>
  );
};

export default Home;
